import type { ICellRendererParams } from 'ag-grid-community';
import { Button } from 'react-bootstrap';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { ResultRow } from '../lib/result-detail';
import { ActionTip } from '../components/ActionTip';
import { IndexerIcon } from '../components/IndexerIcon';
import { copyText, decodeUrlForDisplay, formatTime } from '../lib/utils';

function statusSubtleTone(
  success?: boolean,
): 'success' | 'danger' | 'secondary' {
  if (success === true) return 'success';
  if (success === false) return 'danger';
  return 'secondary';
}

export function resultTableColumnConfig() {
  return [
    {
      field: 'url',
      title: 'آدرس',
      type: 'string',
      flex: 1,
      minWidth: 200,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<ResultRow>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);
        return (
          <span className="d-flex align-items-center h-100 w-100 py-1">
            <span className="mono if-url-clip" title={decoded}>
              {decoded}
            </span>
          </span>
        );
      },
    },
    {
      field: 'label',
      title: 'وضعیت',
      type: 'string',
      width: 96,
      flex: 0,
      cellRenderer: (params: ICellRendererParams<ResultRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span className="d-inline-flex align-items-center py-1">
            <SubtleBadge
              bg={statusSubtleTone(row.success)}
              pill
              className="fs--2"
              style={{ lineHeight: 1.35 }}
            >
              {row.label}
            </SubtleBadge>
          </span>
        );
      },
    },
    {
      field: 'detail',
      title: 'جزئیات',
      type: 'string',
      flex: 1.6,
      minWidth: 220,
      cellClass: 'd-flex align-items-center',
      cellRenderer: (params: ICellRendererParams<ResultRow>) => {
        const row = params.data;
        if (!row) return null;
        const hasMeta = row.httpStatus != null || Boolean(row.typeLabel);
        const hasDetail = Boolean(row.detail);
        const hints = row.hints ?? [];
        if (!hasMeta && !hasDetail && hints.length === 0) {
          return <span className="text-secondary">—</span>;
        }
        return (
          <div className="if-result-detail">
            {(hasMeta || hasDetail) && (
              <div className="if-result-detail-meta">
                {row.httpStatus != null ? (
                  <SubtleBadge
                    bg="secondary"
                    pill
                    className="fs--2 flex-shrink-0"
                    style={{ lineHeight: 1.35 }}
                  >
                    HTTP {row.httpStatus}
                  </SubtleBadge>
                ) : null}
                {row.typeLabel ? (
                  <SubtleBadge
                    bg="info"
                    pill
                    className="fs--2 flex-shrink-0"
                    style={{ lineHeight: 1.35 }}
                  >
                    {row.typeLabel}
                  </SubtleBadge>
                ) : null}
                {hasDetail && row.detailSummary ? (
                  <span className="d-inline-flex align-items-center min-w-0">
                    <span className="if-result-detail-main fs--1 mb-0">
                      {row.detailSummary}
                    </span>
                    <HelpTooltip html={<>{row.detail}</>} placement="left" />
                  </span>
                ) : hasDetail ? (
                  <span className="if-result-detail-main fs--1 mb-0">
                    {row.detail}
                  </span>
                ) : null}
              </div>
            )}
            {hints.length > 0 ? (
              <ul className="if-result-detail-hints">
                <li className="text-secondary fs--2" title={hints.join('\n')}>
                  {hints[0]}
                  {hints.length > 1 ? ` (+${hints.length - 1})` : ''}
                </li>
              </ul>
            ) : null}
          </div>
        );
      },
    },
    {
      field: 'createdAt',
      title: 'زمان',
      type: 'string',
      width: 120,
      flex: 0,
      cellRenderer: (params: ICellRendererParams<ResultRow>) => (
        <span className="text-nowrap">
          {formatTime(params.data?.createdAt)}
        </span>
      ),
    },
    {
      field: '_actions',
      title: 'عملیات',
      width: 56,
      maxWidth: 56,
      sortable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'text-center',
      cellClass: 'd-flex align-items-center justify-content-center',
      cellRenderer: (params: ICellRendererParams<ResultRow>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);
        return (
          <div className="if-table-actions justify-content-center">
            <ActionTip tip="کپی">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="کپی"
                onClick={() => void copyText(decoded)}
              >
                <IndexerIcon name="copy" />
              </Button>
            </ActionTip>
          </div>
        );
      },
    },
  ];
}
